import { createWriteStream } from 'node:fs'
import { resolve } from 'node:path'
import { Readable } from 'node:stream'
import { pipeline } from 'node:stream/promises'
import { Command } from 'commander'
import { createApiClient, SCOPES } from '../http.js'
import { readVpkg } from '../vpkg/read.js'
import {
  createPackage,
  downloadTarball,
  findPackageByName,
  requireMyPublisher,
  resolvePackageRef,
  uploadVersion,
  yankVersion,
} from '../registry.js'
import { loadConfig } from '../config.js'

export function registerRegistry(program: Command): void {
  program
    .command('publish <vpkg>')
    .description('Upload a packed .vpkg to the marketplace registry (creates the package on first publish)')
    .option('--visibility <visibility>', 'catalog visibility for a newly created package: Public | Private', 'Private')
    .action(async (vpkgPath: string, options: { visibility: string }) => {
      const contents = await readVpkg(resolve(vpkgPath))
      if (contents.digestErrors.length > 0) {
        throw new Error(`Refusing to publish: package failed digest verification:\n  ${contents.digestErrors.join('\n  ')}`)
      }

      const api = await createApiClient()
      const publisher = await requireMyPublisher(api)
      if (publisher.name !== contents.metadata.publisher) {
        throw new Error(`Package declares publisher '${contents.metadata.publisher}' but you are '${publisher.name}'.`)
      }

      let pkg = await findPackageByName(api, publisher.id, contents.metadata.name)
      if (!pkg) {
        const visibility = options.visibility === 'Public' ? 'Public' : 'Private'
        const id = await createPackage(api, {
          publisherId: publisher.id,
          name: contents.metadata.name,
          displayName: contents.metadata['display-name'] ?? contents.metadata.name,
          kind: contents.metadata.kind,
          visibility,
        })
        console.log(`Created package ${publisher.name}/${contents.metadata.name} (${visibility.toLowerCase()}).`)
        pkg = { id } as NonNullable<typeof pkg>
      }

      const version = await uploadVersion(api, pkg.id, resolve(vpkgPath))
      console.log(`Published ${publisher.name}/${contents.metadata.name}@${version.version} → ${version.status}`)
      if (version.status === 'Submitted') {
        console.log('The version is queued for moderation; it becomes installable once approved and published.')
      }
    })

  program
    .command('yank <ref> <version>')
    .description("Withdraw a published version from the catalog (ref: 'name' or 'publisher/name')")
    .action(async (ref: string, version: string) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      await yankVersion(api, pkg.id, version)
      console.log(`Yanked ${ref}@${version}.`)
    })

  program
    .command('download <ref> [version]')
    .description('Download a package tarball (defaults to the latest published version)')
    .option('--out <path>', 'output file path')
    .option('--workspace <workspaceId>', 'workspace context for visibility checks')
    .action(async (ref: string, version: string | undefined, options: { out?: string; workspace?: string }) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      const effectiveVersion = version ?? pkg.latestPublishedVersion ?? undefined
      if (!effectiveVersion) throw new Error(`Package '${ref}' has no published version.`)

      const res = await downloadTarball(api, pkg.id, effectiveVersion, options.workspace)
      const outPath = resolve(options.out ?? `${pkg.name}-${effectiveVersion}.vpkg`)
      if (!res.body) throw new Error('Empty download response.')
      await pipeline(Readable.fromWeb(res.body as import('node:stream/web').ReadableStream), createWriteStream(outPath))
      console.log(`Downloaded ${ref}@${effectiveVersion} → ${outPath}`)
    })

  program
    .command('search [text]')
    .description('Search the marketplace catalog')
    .option('--kind <kind>', 'Application | ExecutionEngine | Bundle')
    .action(async (text: string | undefined, options: { kind?: string }) => {
      const api = await createApiClient()
      const page = await api.request<{ data?: unknown[]; items?: unknown[] }>('GET', '/v1/packages', {
        scopes: [SCOPES.packageReadAll],
        query: { SearchText: text, Kind: options.kind, PageNo: 1, PageSize: 50 },
      })
      const items = (page.data ?? page.items ?? []) as { publisherName: string; name: string; displayName: string; kind: string; visibility: string; latestPublishedVersion?: string }[]
      if (items.length === 0) {
        console.log('No packages found.')
        return
      }
      for (const pkg of items) {
        const version = pkg.latestPublishedVersion ? `@${pkg.latestPublishedVersion}` : ' (no published version)'
        console.log(`${pkg.publisherName}/${pkg.name}${version} — ${pkg.displayName} [${pkg.kind}, ${pkg.visibility.toLowerCase()}]`)
      }
    })

  program
    .command('install <ref> [version]')
    .description('Install a package into a workspace')
    .option('--workspace <workspaceId>', 'target workspace (default: configured default workspace)')
    .option('--path <path>', 'install path override (bundles)')
    .option('--branch <branch>', 'workspace branch', 'main')
    .option('--sealed', 'install as a sealed tarball instead of extracting')
    .action(async (ref: string, version: string | undefined, options: { workspace?: string; path?: string; branch: string; sealed?: boolean }) => {
      const config = await loadConfig()
      const workspaceId = options.workspace ?? config.defaultWorkspaceId
      if (!workspaceId) throw new Error('No workspace: pass --workspace <id> or set defaultWorkspaceId in ~/.verentis/config.json.')

      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      const installationId = await api.request<string>('POST', '/v1/installations', {
        // Marketplace forwards this token to Node for the extraction, so it needs
        // the node scope and must be bound to the target workspace.
        scopes: [SCOPES.installCreate, 'node.node.create'],
        tokenWorkspaceId: workspaceId,
        body: {
          workspaceId,
          packageId: pkg.id,
          version: version ?? null,
          installPath: options.path ?? null,
          branch: options.branch,
          installMode: options.sealed ? 'Sealed' : 'Extracted',
        },
      })
      console.log(`Installation ${installationId} started for ${ref} in workspace ${workspaceId}.`)
    })

  program
    .command('uninstall <installationId>')
    .description('Uninstall a package from a workspace')
    .requiredOption('--workspace <workspaceId>', 'the workspace the installation belongs to')
    .action(async (installationId: string, options: { workspace: string }) => {
      const api = await createApiClient()
      await api.request('DELETE', `/v1/installations/${installationId}`, {
        scopes: [SCOPES.installDelete, 'node.file.delete'],
        tokenWorkspaceId: options.workspace,
        body: { id: installationId, workspaceId: options.workspace },
      })
      console.log(`Uninstalled ${installationId}.`)
    })
}
